import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Plus, Ticket, CheckSquare, Clock, AlertTriangle, Hourglass, CheckCircle,
    Sparkles, ShieldAlert, Users, ArrowRight, Zap,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { StatusBadge, PriorityBadge, DeadlineBadge } from "../shared/Badge";
import { UserAvatar } from "../shared/UserAvatar";
import { EmptyState } from "../shared/EmptyState";
import { PageLoader } from "../shared/LoadingSpinner";
import { formatRelativeTime, getTicketTypeConfig } from "../../lib/utils";

export function Dashboard() {
    const { currentUser, getUserById, hasPermission, wsClient, isTeamSupervisor, supervisedDeptIds, supervisedDepartments } = useApp();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [recentTickets, setRecentTickets] = useState([]);
    const [pendingApprovals, setPendingApprovals] = useState([]);
    const [loading, setLoading] = useState(true);

    const isAgentOrAdmin = hasPermission("agent");
    const isCoordinatorOrAbove = hasPermission("coordinator");

    useEffect(() => {
        if (currentUser) {
            loadData();
        }
    }, [currentUser]);

    // 10B — WebSocket: auto-refresh dashboard on ticket events
    useEffect(() => {
        if (!wsClient) return;
        const refresh = () => { if (currentUser) loadData(); };
        const unsub1 = wsClient.on("ticket_created", refresh);
        const unsub2 = wsClient.on("ticket_updated", refresh);
        const unsub3 = wsClient.on("ticket_assigned", refresh);
        return () => { unsub1(); unsub2(); unsub3(); };
    }, [wsClient, currentUser]);

    const loadData = async () => {
        try {
            setLoading(true);
            const deptIdsStr = isTeamSupervisor ? supervisedDeptIds.join(",") : undefined;
            const results = await Promise.allSettled([
                api.getDashboardStats(currentUser.id, deptIdsStr),
                api.getTickets({ involved_user_id: currentUser.id, limit: 5 }),
                api.getPendingApprovals(currentUser.id).catch(() => []),
            ]);

            const statsData = results[0].status === "fulfilled" ? results[0].value : null;
            const ticketsData = results[1].status === "fulfilled" ? results[1].value : [];
            const approvalsData = results[2].status === "fulfilled" ? results[2].value : [];

            if (statsData) setStats(statsData);
            setRecentTickets(ticketsData);
            setPendingApprovals(approvalsData);
        } catch (error) {
            console.error("Failed to load dashboard:", error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <PageLoader />;

    const userStats = stats?.user_stats || {};
    const urgentTickets = stats?.urgent_tickets || [];
    const newTicketsCount = stats?.new_tickets_count || 0;
    const globalRecentTickets = stats?.recent_tickets || [];
    const teamStats = stats?.team_stats || {};
    const teamRecentTickets = teamStats.team_recent_tickets || [];

    return (
        <div className="page-container" data-testid="dashboard">
            {/* Header */}
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Tableau de bord</h1>
                    <p className="page-subtitle">
                        Bonjour {currentUser?.first_name}, voici votre activit&eacute;
                    </p>
                </div>
                <Link to="/nouveau">
                    <Button data-testid="new-ticket-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Nouveau ticket
                    </Button>
                </Link>
            </div>

            {/* 10C — Alert Banners (admin/agent/coordinator) */}
            {isCoordinatorOrAbove && (stats?.overdue_count > 0 || urgentTickets.length > 0) && (
                <div className="space-y-3 mb-6">
                    {stats?.overdue_count > 0 && (
                        <button
                            onClick={() => navigate("/admin/tickets?sort=deadline")}
                            className="w-full flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors text-left"
                            data-testid="alert-overdue"
                        >
                            <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-red-900">{stats.overdue_count} ticket(s) en retard</p>
                                <p className="text-xs text-red-700">Date limite dépassée — cliquez pour voir la file</p>
                            </div>
                            <ArrowRight className="w-4 h-4 text-red-400" />
                        </button>
                    )}
                    {newTicketsCount > 0 && (
                        <button
                            onClick={() => navigate("/admin/tickets?status=new")}
                            className="w-full flex items-center gap-3 px-4 py-3 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors text-left"
                            data-testid="alert-new-tickets"
                        >
                            <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-purple-900">{newTicketsCount} nouveau(x) ticket(s) non consult&eacute;(s)</p>
                                <p className="text-xs text-purple-700">Ces tickets n'ont pas encore &eacute;t&eacute; vus par un agent</p>
                            </div>
                            <ArrowRight className="w-4 h-4 text-purple-400" />
                        </button>
                    )}
                </div>
            )}

            {/* Stats Grid — KPI cards */}
            <div className="stats-grid mb-8">
                <button onClick={() => navigate("/tickets")} className="stat-card text-left cursor-pointer hover:ring-2 hover:ring-gray-200 transition-all" data-testid="stat-my-tickets">
                    <div className="flex items-center gap-2">
                        <Ticket className="w-4 h-4 text-gray-500" />
                        <div className="stat-label">Mes tickets</div>
                    </div>
                    <div className="stat-value">{userStats.my_tickets || 0}</div>
                </button>
                <button onClick={() => navigate("/tickets?status=open")} className="stat-card text-left cursor-pointer hover:ring-2 hover:ring-gray-200 transition-all" data-testid="stat-in-progress">
                    <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-blue-500" />
                        <div className="stat-label">En cours</div>
                    </div>
                    <div className="stat-value">{userStats.my_in_progress || 0}</div>
                </button>
                <button onClick={() => navigate("/tickets?status=pending_user")} className="stat-card text-left cursor-pointer hover:ring-2 hover:ring-gray-200 transition-all" data-testid="stat-pending">
                    <div className="flex items-center gap-2">
                        <Hourglass className="w-4 h-4 text-yellow-500" />
                        <div className="stat-label">En attente</div>
                    </div>
                    <div className="stat-value">{userStats.my_pending || 0}</div>
                </button>
                <button onClick={() => navigate("/tickets?status=resolved")} className="stat-card text-left cursor-pointer hover:ring-2 hover:ring-gray-200 transition-all" data-testid="stat-done">
                    <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <div className="stat-label">Termin&eacute;s</div>
                    </div>
                    <div className="stat-value">{userStats.my_done || 0}</div>
                </button>
                {userStats.pending_approvals > 0 && (
                    <button onClick={() => navigate("/approbations")} className="stat-card text-left cursor-pointer hover:ring-2 hover:ring-gray-200 transition-all" data-testid="stat-pending-approvals">
                        <div className="flex items-center gap-2">
                            <CheckSquare className="w-4 h-4 text-purple-500" />
                            <div className="stat-label">Approbations</div>
                        </div>
                        <div className="stat-value">{userStats.pending_approvals || 0}</div>
                    </button>
                )}
                {isAgentOrAdmin && (
                    <button onClick={() => navigate("/admin/tickets?mine=true")} className="stat-card text-left cursor-pointer hover:ring-2 hover:ring-gray-200 transition-all" data-testid="stat-assigned">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-orange-500" />
                            <div className="stat-label">Assign&eacute;s &agrave; moi</div>
                        </div>
                        <div className="stat-value">{userStats.assigned_to_me || 0}</div>
                    </button>
                )}
            </div>

            {/* Team supervisor section */}
            {!isCoordinatorOrAbove && isTeamSupervisor && (
                <div className="mb-6">
                    <div className="stats-grid mb-4">
                        <button onClick={() => navigate("/tickets?view=team")} className="stat-card text-left cursor-pointer hover:ring-2 hover:ring-teal-200 transition-all" data-testid="stat-team-tickets">
                            <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-teal-500" />
                                <div className="stat-label">Tickets &eacute;quipe</div>
                            </div>
                            <div className="stat-value text-teal-600">{teamStats.team_tickets || 0}</div>
                        </button>
                        <div className="stat-card" data-testid="stat-team-open">
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-blue-500" />
                                <div className="stat-label">&Eacute;quipe — En cours</div>
                            </div>
                            <div className="stat-value">{teamStats.team_open || 0}</div>
                        </div>
                        <div className="stat-card" data-testid="stat-team-pending">
                            <div className="flex items-center gap-2">
                                <Hourglass className="w-4 h-4 text-yellow-500" />
                                <div className="stat-label">&Eacute;quipe — En attente</div>
                            </div>
                            <div className="stat-value">{teamStats.team_pending || 0}</div>
                        </div>
                        <div className="stat-card" data-testid="stat-team-done">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <div className="stat-label">&Eacute;quipe — Termin&eacute;s</div>
                            </div>
                            <div className="stat-value">{teamStats.team_done || 0}</div>
                        </div>
                    </div>
                    {teamRecentTickets.length > 0 && (
                        <div className="card">
                            <div className="card-header flex items-center justify-between">
                                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                    <Users className="w-4 h-4 text-teal-500" />
                                    Tickets de mon &eacute;quipe
                                </h2>
                                <Link to="/tickets?view=team" className="text-xs font-medium text-gray-500 hover:text-gray-900">Voir tout</Link>
                            </div>
                            <div className="card-body p-0">
                                <div className="divide-y divide-gray-100">
                                    {teamRecentTickets.slice(0, 5).map((ticket) => {
                                        const typeConfig = getTicketTypeConfig(ticket.type);
                                        const req = getUserById(ticket.requester_id);
                                        return (
                                            <Link
                                                key={ticket.id}
                                                to={`/tickets/${ticket.id}`}
                                                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                                                data-testid={`team-ticket-${ticket.id}`}
                                            >
                                                <UserAvatar user={req} size="sm" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs font-mono text-gray-400">{ticket.ticket_number}</span>
                                                        <span className="text-xs text-gray-400">{typeConfig.label}</span>
                                                    </div>
                                                    <p className="text-sm text-gray-900 truncate">{ticket.title}</p>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <StatusBadge status={ticket.status} />
                                                    <PriorityBadge priority={ticket.priority} />
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* 10C — Admin Command Center: urgent tickets table */}
            {isCoordinatorOrAbove && urgentTickets.length > 0 && (
                <div className="card mb-6">
                    <div className="card-header flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-amber-500" />
                            <h2 className="text-sm font-semibold text-gray-900">Tickets urgents</h2>
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{urgentTickets.length}</span>
                        </div>
                        <Link to="/admin/tickets?sort=sla_urgency" className="text-xs font-medium text-gray-500 hover:text-gray-900">Voir tout</Link>
                    </div>
                    <div className="card-body p-0">
                        <div className="divide-y divide-gray-100">
                            {urgentTickets.slice(0, 5).map((ticket) => {
                                const typeConfig = getTicketTypeConfig(ticket.type);
                                const assignee = getUserById(ticket.assigned_to_id);
                                return (
                                    <Link
                                        key={ticket.id}
                                        to={`/tickets/${ticket.id}`}
                                        className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition-colors"
                                        data-testid={`urgent-ticket-${ticket.id}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <span className="text-xs font-mono text-gray-400">{ticket.ticket_number}</span>
                                                <span className="text-xs text-gray-400">{typeConfig.label}</span>
                                            </div>
                                            <p className="text-sm font-medium text-gray-900 truncate">{ticket.title}</p>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {assignee ? (
                                                <UserAvatar user={assignee} size="sm" />
                                            ) : (
                                                <span className="text-xs text-red-500 font-medium bg-red-50 px-2 py-0.5 rounded">Non assign&eacute;</span>
                                            )}
                                            <DeadlineBadge info={ticket.deadline_info} />
                                            <StatusBadge status={ticket.status} />
                                            <PriorityBadge priority={ticket.priority} />
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Tickets (user's own) */}
                <div className="card">
                    <div className="card-header flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-gray-900">Mes tickets r&eacute;cents</h2>
                        <Link to="/tickets" className="text-xs font-medium text-gray-500 hover:text-gray-900">Voir tout</Link>
                    </div>
                    <div className="card-body p-0">
                        {recentTickets.length === 0 ? (
                            <EmptyState icon={Ticket} title="Aucun ticket" description="Vous n'avez pas encore cr&eacute;&eacute; de ticket" />
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {recentTickets.map((ticket) => {
                                    const typeConfig = getTicketTypeConfig(ticket.type);
                                    return (
                                        <Link
                                            key={ticket.id}
                                            to={`/tickets/${ticket.id}`}
                                            className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
                                            data-testid={`ticket-row-${ticket.id}`}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-xs font-mono text-gray-400">{ticket.ticket_number}</span>
                                                    <span className="text-xs text-gray-400">{typeConfig.label}</span>
                                                </div>
                                                <p className="text-sm font-medium text-gray-900 truncate">{ticket.title}</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <DeadlineBadge info={ticket.deadline_info} />
                                                <StatusBadge status={ticket.status} />
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Pending Approvals / Activity */}
                <div className="card">
                    <div className="card-header flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-gray-900">
                            {isCoordinatorOrAbove ? "Activité récente" : "Approbations en attente"}
                        </h2>
                        <Link to={isCoordinatorOrAbove ? "/admin/tickets" : "/approbations"} className="text-xs font-medium text-gray-500 hover:text-gray-900">Voir tout</Link>
                    </div>
                    <div className="card-body p-0">
                        {isCoordinatorOrAbove ? (
                            // Admin/Agent: show global recent activity
                            globalRecentTickets.length === 0 ? (
                                <EmptyState icon={Ticket} title="Aucune activité" description="Pas d'activité récente" />
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {globalRecentTickets.slice(0, 7).map((ticket) => {
                                        const typeConfig = getTicketTypeConfig(ticket.type);
                                        const requester = getUserById(ticket.requester_id);
                                        return (
                                            <Link
                                                key={ticket.id}
                                                to={`/tickets/${ticket.id}`}
                                                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                                                data-testid={`recent-activity-${ticket.id}`}
                                            >
                                                <UserAvatar user={requester} size="sm" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs font-mono text-gray-400">{ticket.ticket_number}</span>
                                                        <span className="text-xs text-gray-400">{typeConfig.label}</span>
                                                    </div>
                                                    <p className="text-sm text-gray-900 truncate">{ticket.title}</p>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <StatusBadge status={ticket.status} />
                                                    <span className="text-xs text-gray-400">{formatRelativeTime(ticket.created_at)}</span>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            )
                        ) : (
                            // User: show pending approvals
                            pendingApprovals.length === 0 ? (
                                <EmptyState icon={CheckSquare} title="Aucune approbation" description="Vous n'avez aucune approbation en attente" />
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {pendingApprovals.slice(0, 5).map((approval) => {
                                        const ticket = approval.ticket;
                                        const requester = getUserById(ticket?.requester_id);
                                        return (
                                            <Link
                                                key={approval.id}
                                                to={`/tickets/${ticket?.id}`}
                                                className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
                                                data-testid={`approval-row-${approval.id}`}
                                            >
                                                <UserAvatar user={requester} size="md" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">{ticket?.title}</p>
                                                    <p className="text-xs text-gray-500">
                                                        Demand&eacute; par {requester?.first_name} {requester?.last_name}
                                                    </p>
                                                </div>
                                                <PriorityBadge priority={ticket?.priority} />
                                            </Link>
                                        );
                                    })}
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>

            {/* Admin Stats — Overview */}
            {isCoordinatorOrAbove && (
                <div className="mt-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Vue d'ensemble</h2>
                    <div className="stats-grid">
                        <button onClick={() => navigate("/admin/tickets?status=new")} className="stat-card text-left cursor-pointer hover:ring-2 hover:ring-purple-200 transition-all" data-testid="stat-global-new">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-purple-500" />
                                <div className="stat-label">Nouveaux</div>
                            </div>
                            <div className="stat-value text-purple-600">{stats?.by_status?.new || 0}</div>
                        </button>
                        <button onClick={() => navigate("/admin/tickets?status=open")} className="stat-card text-left cursor-pointer hover:ring-2 hover:ring-gray-200 transition-all" data-testid="stat-global-open">
                            <div className="flex items-center gap-2">
                                <Ticket className="w-4 h-4 text-blue-500" />
                                <div className="stat-label">Total ouvert</div>
                            </div>
                            <div className="stat-value">{stats?.total_open || 0}</div>
                        </button>
                        <button onClick={() => navigate("/admin/tickets?sort=deadline")} className="stat-card text-left cursor-pointer hover:ring-2 hover:ring-red-200 transition-all" data-testid="stat-global-overdue">
                            <div className="flex items-center gap-2">
                                <ShieldAlert className="w-4 h-4 text-red-500" />
                                <div className="stat-label">En retard</div>
                            </div>
                            <div className="stat-value text-red-600">{stats?.overdue_count || 0}</div>
                        </button>
                        <button onClick={() => navigate("/admin/tickets?type=incident")} className="stat-card text-left cursor-pointer hover:ring-2 hover:ring-gray-200 transition-all" data-testid="stat-global-incidents">
                            <div className="flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-orange-500" />
                                <div className="stat-label">Incidents</div>
                            </div>
                            <div className="stat-value">{stats?.by_type?.incident || 0}</div>
                        </button>
                        <button onClick={() => navigate("/admin/tickets?status=pending_approval")} className="stat-card text-left cursor-pointer hover:ring-2 hover:ring-gray-200 transition-all" data-testid="stat-global-pending-approval">
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-amber-500" />
                                <div className="stat-label">En attente appro.</div>
                            </div>
                            <div className="stat-value">{stats?.by_status?.pending_approval || 0}</div>
                        </button>
                        <button onClick={() => navigate("/admin/tickets?status=resolved")} className="stat-card text-left cursor-pointer hover:ring-2 hover:ring-gray-200 transition-all" data-testid="stat-global-resolved">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <div className="stat-label">R&eacute;solus</div>
                            </div>
                            <div className="stat-value">{stats?.by_status?.resolved || 0}</div>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
